import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLEndorsementFlowUtil, MLEndorsementUtil } from 'ml-portals-util-js';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { MLPolicyChangeDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { UnderlyingPolicyComponent } from 'ml-capability-quoteandbind-common-react';
import metadata from './YourPoliciesPagePolChange.metadata.json5';

import messages from './YourPoliciesPagePolChange.messages';
import styles from './YourPoliciesPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const alertNames = {
    addedUnderlyingPolicyName: 'addedUnderlyingPolicy',
    removedUnderlyingPolicyName: 'removedUnderlyingPolicy'
};

const YourPoliciesPagePolChange = (props) => {
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        currentStep,
        changeNextSteps
    } = props;
    const { submissionVM, alertMessages } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const POLICYTYPE_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('PUPULPolicyType_PUE');
    const CARRIER_TYPELIST = viewModelService.productMetadata.get('pc').types.getTypelist('ULCarrier_PUE');
    
    const {
        showProgressDialog
    } = MLModalService();

    const MLAlertService = MLNotificationBlockUtil();

    const isGPA = appConfig.portalCode === 'GPA';
    const underlyingPoliciesVM = _.get(submissionVM, 'lobData.personalUmbrella.underlyingPolicies');
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);

    const initCurrentUlPolicyVM = () => {

        const { additionalData } = currentStep;
        let underlyingPolicyVM ;
        const selectedUnderlyingPolicyVM = underlyingPoliciesVM.children.find((ul) => ul.value.isSelected === true);

        if (additionalData) {
            // if Additional Data is availabe
            underlyingPolicyVM = underlyingPoliciesVM.children.find((wc) => wc.value.fixedID === additionalData.fixedID);
        }

        if (!_.isUndefined(selectedUnderlyingPolicyVM)) {
            // if the isSelected is available
            underlyingPolicyVM = selectedUnderlyingPolicyVM
        }

        return underlyingPolicyVM
    };

    const [ulPolicy] = useState(initCurrentUlPolicyVM()); 
    const [pageAction, setPageAction] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);

    const { onValidate , isComponentValid } = useValidation('YourPoliciesPagePolChange');

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const initYourPoliciesPage = () => {
        if (MLPolicyChangeDataHolderService.getPageAction() === 'add') {
            setPageAction('add');
            MLNavigationStepsTrackingService.addToDynamicSteps(_.startCase(translator(messages.addPolicy)), false, currentStep.mlStepName, authHeader);
        } else if (MLPolicyChangeDataHolderService.getPageAction() === 'edit') {
            setPageAction('edit');            
            MLNavigationStepsTrackingService.addToDynamicSteps(_.startCase(translator(messages.editPolicy)), false, currentStep.mlStepName, authHeader);
        } 
    };

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        initYourPoliciesPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const removeAlert = (id) => {
        const index = alertMessages.findIndex((alert) => alert.id === id);
        if (index >= 0) {
            alertMessages.splice(index, 1);
        }
    };

    const addNewUnderlyingPolicyAlert = (item) => {
        const alertMsg = {
            alertName: alertNames.addedUnderlyingPolicyName,
            formattedMessageName: 'xHasBeenAdded',
            messagePram: item.value.getDisplayNameShort(viewModelService.productMetadata, translator)
        };
        removeAlert(alertNames.addedUnderlyingPolicyName);
        removeAlert(alertNames.removedUnderlyingPolicyName);
        alertMessages.push(alertMsg);
    };

    const getTypeKeyDescription = useCallback((typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    }, [translator]);

    const getNameOfCarrier = (item) => {
        let carrier = null;
        if (item.carrier) {
            carrier = getTypeKeyDescription(CARRIER_TYPELIST.getCode(item.carrier).name);
            if (carrier === 'Other') {
                carrier = item.otherCarrier;
            }
        }
        return carrier;
    };

    const jumpToSelectedStepByStepID = (newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode);
    };

    const onNext = () => {
        if (!(ulPolicy.aspects.valid && ulPolicy.aspects.subtreeValid && !ulPolicy.coveragesError) || !isComponentValid) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }

        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saving, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            
            if (pageAction === 'add') {
                addNewUnderlyingPolicyAlert(ulPolicy);
            }
            updateWizardSubmissionData(updatedEndorsementVM);   
            const item = ulPolicy.value;
            const nameOfCareer = getNameOfCarrier(item);
            const nameOfPolicyType = item.policyType ? getTypeKeyDescription(POLICYTYPE_TYPELIST.getCode(item.policyType).name) : null;
            const details = {
                data : `${nameOfCareer} ${nameOfPolicyType}`,
                additionalData : {fixedID: ulPolicy.value.fixedID}
            }
            
            MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader,currentStep);               
            const lbl = MLEndorsementUtil.stepLabel(currentStep);
            MLEndorsementUtil.setEndorsementDescription(lbl, updatedEndorsementVM, translator);
            MLTagManagerService.sendFormStepPolicyChangeStep(`${pageAction} Policy`);
            jumpToSelectedStepByStepID('endorsementAdditionalChanges');
        })

    };

    const resolvers = {
        resolveComponentMap: {
            underlyingPolicyComponent: UnderlyingPolicyComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        addEditHeaderTitle: {
            visible: pageAction === 'add' || pageAction === 'edit',
            content:  pageAction === 'add' ? translator(messages.addPolicy) : translator(messages.editPolicy)
        },
        underlyingPolicyComponent: {
            underlyingPolicyVM: ulPolicy,
            quoteID: submissionVM.value.jobID,
            isModernProduct,
            isPolicyChange: true,
            onValidate,
            showValidationErrors,
            isEdit: pageAction === 'edit',
            authHeader,
            isComponent: true,
            MLAlertService
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isGPA ? messages.makeChanges : null}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );

};

YourPoliciesPagePolChange.propTypes = wizardProps;
export default YourPoliciesPagePolChange;